import { useMemo, useState } from 'react';
import * as x509 from '@peculiar/x509';
import { ConfirmationPopup, Icon } from '@timeedit/ui-components';
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Input,
  List,
  Modal,
  Space,
  Typography,
  theme,
} from '@timeedit/ui-components/antd';

interface CertificateListProps {
  encodedCertificates: string[];
  disabled?: boolean;
  onAdd?: (encodedCertificate: string) => void;
  onRemove?: (encodedCertificate: string) => void;
}

interface DecodedCertificate {
  encoded: string;
  notBefore: Date;
  notAfter: Date;
  subject?: string;
}

const { TextArea } = Input;
const { Text } = Typography;
const { useToken } = theme;

const expiryWarningThresholdDays = 60;

const sanitizeRawCert = (value: string): string => {
  return value.trim().replace(/\s+/g, '');
};

const decodeCertificate = (
  certString: string
): DecodedCertificate | undefined => {
  if (!certString) {
    return;
  }

  const encodedSanitizedCert = sanitizeRawCert(certString);

  try {
    const decodedCert = new x509.X509Certificate(encodedSanitizedCert);
    return {
      encoded: encodedSanitizedCert,
      notBefore: decodedCert.notBefore,
      notAfter: decodedCert.notAfter,
      subject: decodedCert.subject,
    };
  } catch (error) {
    console.log('Failed to decode certificate', error);
    return;
  }
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isExpired = (date: Date): boolean => {
  return date < new Date();
};

const daysBetween = (date1: Date, date2: Date): number => {
  const oneDay = 24 * 60 * 60 * 1000;
  const diffDays = Math.round(
    Math.abs((date2.getTime() - date1.getTime()) / oneDay)
  );
  return diffDays;
};

const CertificateList: React.FC<CertificateListProps> = ({
  encodedCertificates: certificates,
  disabled = false,
  onAdd,
  onRemove,
}: CertificateListProps) => {
  const { token } = useToken();

  const [isNewCertModalOpen, setIsNewCertModalOpen] = useState(false);
  const [certInput, setCertInput] = useState('');
  const [decodedCert, setDecodedCert] = useState<DecodedCertificate | null>(
    null
  );
  const [isDecodeError, setIsDecodeError] = useState(false);
  const [isDuplicateCert, setIsDuplicateCert] = useState(false);
  const [isCertDetailsModalOpen, setIsCertDetailsModalOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState<string | null>(null);
  const decodedCertificates = useMemo(
    () =>
      certificates
        .map((rawCert) => decodeCertificate(rawCert))
        .filter((cert): cert is DecodedCertificate => cert !== undefined),
    [certificates]
  );

  const handleShowCertDetails = (certString: string) => {
    setSelectedCert(certString);
    setIsCertDetailsModalOpen(true);
  };

  const handleCertInputChange = (cert: string) => {
    const sanitizedCert = sanitizeRawCert(cert);
    setCertInput(sanitizedCert);

    const isDuplicate = decodedCertificates.some(
      (existingCert) => existingCert.encoded === sanitizedCert
    );
    if (isDuplicate) {
      setDecodedCert(null);
      setIsDecodeError(false);
      setIsDuplicateCert(true);
      return;
    }

    setIsDuplicateCert(false);

    if (!sanitizedCert) {
      setDecodedCert(null);
      setIsDecodeError(false);
      return;
    }

    const decoded = decodeCertificate(sanitizedCert);
    if (decoded) {
      setDecodedCert(decoded);
      setIsDecodeError(false);
    } else {
      setDecodedCert(null);
      setIsDecodeError(true);
    }
  };

  const handleOpenNewCertModal = () => {
    setIsNewCertModalOpen(true);
    setCertInput('');
    setDecodedCert(null);
    setIsDuplicateCert(false);
    setIsDecodeError(false);
  };

  const handleCloseNewCertModal = () => {
    setIsNewCertModalOpen(false);
    setCertInput('');
    setDecodedCert(null);
    setIsDuplicateCert(false);
    setIsDecodeError(false);
  };

  const handleAddCertificate = () => {
    if (decodedCert && onAdd) {
      onAdd(certInput);
      handleCloseNewCertModal();
    }
  };

  const handleRemoveCertificate = (id: string) => {
    if (onRemove) {
      onRemove(id);
    }
  };

  const getCertificateStatus = (
    cert: DecodedCertificate
  ): 'valid' | 'expired' | 'expiring' => {
    if (isExpired(cert.notAfter)) {
      return 'expired';
    }
    if (daysBetween(new Date(), cert.notAfter) <= expiryWarningThresholdDays) {
      return 'expiring';
    }

    return 'valid';
  };

  const getCertificateTextType = (cert: DecodedCertificate) => {
    const status = getCertificateStatus(cert);
    if (status === 'expired') {
      return 'danger';
    }
    if (status === 'expiring') {
      return 'warning';
    }
    return 'success';
  };

  const getCertificateIconType = (cert: DecodedCertificate) => {
    const status = getCertificateStatus(cert);
    if (status === 'expired') {
      return token.colorError;
    }
    if (status === 'expiring') {
      return token.colorWarning;
    }

    return token.colorSuccess;
  };

  return (
    <ConfigProvider componentDisabled={disabled}>
      <Card
        title={`Certificates (${certificates.length})`}
        actions={[
          <div key="create" className="tw:flex tw:ml-2">
            <Button
              type="default"
              onClick={handleOpenNewCertModal}
              size="small"
            >
              Add Certificate
            </Button>
          </div>,
        ]}
      >
        <List
          dataSource={decodedCertificates}
          locale={{ emptyText: 'No certificates added yet' }}
          renderItem={(cert) => (
            <List.Item
              actions={[
                <ConfirmationPopup
                  key="remove"
                  icon={<Icon alt="Warning" id="Warning" />}
                  message="Are you sure you want to remove this certificate?"
                  onConfirm={() => handleRemoveCertificate(cert.encoded)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    type="default"
                    danger
                    icon={<Icon id="DeleteOutlined" alt="Delete" />}
                  />
                </ConfirmationPopup>,
                <Button
                  type="default"
                  key="show"
                  icon={<Icon id="InfoCircleOutlined" alt="Info Circle" />}
                  onClick={() => handleShowCertDetails(cert?.encoded)}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Icon
                    id="SafetyCertificateOutlined"
                    alt="Safety Certificate"
                    style={{
                      fontSize: 24,
                      color: getCertificateIconType(cert),
                    }}
                  />
                }
                title={
                  <Space direction="vertical" size={2}>
                    <Text strong>{cert.subject || '(No Subject)'}</Text>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={4}>
                    <Text>
                      <Text type="secondary">Created: </Text>
                      <Text strong>{formatDate(cert.notBefore)}</Text>
                    </Text>
                    <Text>
                      <Text type="secondary">Expires: </Text>
                      <Text strong type={getCertificateTextType(cert)}>
                        {formatDate(cert.notAfter)}
                        {isExpired(cert.notAfter) && ' (Expired)'}
                        {daysBetween(new Date(), cert.notAfter) <=
                          expiryWarningThresholdDays &&
                          !isExpired(cert.notAfter) &&
                          ' (Expiring Soon)'}
                      </Text>
                    </Text>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>

      <Modal
        title="Add New Certificate"
        open={isNewCertModalOpen}
        onCancel={handleCloseNewCertModal}
        onOk={handleAddCertificate}
        okText="Add"
        okButtonProps={{ disabled: !decodedCert }}
        width={700}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <TextArea
            rows={8}
            placeholder="Paste X.509 certificate here (without BEGIN/END markers), example:&#10;MIIDXTCCAkWgAwIBAgIJAKL0UG+mRkSvMA0GCSqGSIb3...&#10;"
            value={certInput}
            onChange={(e) => handleCertInputChange(e.target.value)}
          />

          {isDecodeError && (
            <Alert
              title="Unable to decode the certificate"
              description="Make sure you pasted the entire certificate, without any missing or extra characters. Spaces and newlines will be removed automatically."
              type="error"
              icon={<Icon id="Error" alt="Error" />}
              showIcon
            />
          )}

          {isDuplicateCert && (
            <Alert
              title="Duplicate certificate"
              description="That certificate has already been added."
              type="error"
              icon={<Icon id="Error" alt="Error" />}
              showIcon
            />
          )}

          {decodedCert && (
            <Alert
              title="Certificate decode successfully"
              description={
                <Space direction="vertical" size={4}>
                  <Text>
                    <Text strong>Created: </Text>
                    {formatDate(decodedCert.notBefore)}
                  </Text>
                  <Text>
                    <Text strong>Expires: </Text>
                    <Text
                      type={
                        isExpired(decodedCert.notAfter) ? 'danger' : 'success'
                      }
                    >
                      {formatDate(decodedCert.notAfter)}
                      {isExpired(decodedCert.notAfter) && ' (EXPIRED)'}
                    </Text>
                  </Text>
                </Space>
              }
              type="success"
              icon={<Icon id="Success" alt="Success" />}
              showIcon
            />
          )}
        </Space>
      </Modal>
      <Modal
        title="Certificate Details"
        open={isCertDetailsModalOpen}
        width={700}
        onCancel={() => setIsCertDetailsModalOpen(false)}
        footer={[
          <Button key="ok" onClick={() => setIsCertDetailsModalOpen(false)}>
            Done
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <TextArea
            value={selectedCert || ''}
            disabled={true}
            autoSize={{ minRows: 8 }}
          />
        </Space>
      </Modal>
    </ConfigProvider>
  );
};

export default CertificateList;
